import { CaretRight, ChatCircleText, CheckCircle, HandPalm, XCircle } from '@phosphor-icons/react';
import { Badge } from '~/components/ui/badge';
import { AnsweredView, RecommendedChip, splitRecommended } from '~/features/chat/questions-card';
import type { CodingStatus, TranscriptItem } from './coding-events';

type QuestionsItem = Extract<TranscriptItem, { kind: 'questions' }>;

type QuestionsMode = QuestionsItem['mode'];

/**
 * Pure — whether this card still accepts input and can be submitted.
 *
 * Extracted under FRONT-1685 (layer 4) precisely so the rule is testable: the
 * frontend suite runs under plain node with no jsdom, so an inline derivation
 * inside the component could not be exercised at all, and this one had no
 * coverage whatsoever.
 *
 * The signature is the guard. A batch's OWN resolution retires it —
 * answered (including a Skip, which the backend records as an answer, and a
 * build-mode self-answer), stopped, or a submit that genuinely 409'd — and so
 * does the death of the run it belongs to (`runStatus`, below). Whether some
 * NEWER batch has since appeared elsewhere in the tape is deliberately not a
 * parameter, so it cannot be consulted: the bug was `&& interactive` here,
 * which froze an older still-unanswered card's inputs and hid its Submit row
 * the moment a sibling batch showed up.
 *
 * That mattered more than a frozen form. The interview poll
 * (`src/coding/ask-user-tool.ts`) times out after ten minutes WITHOUT
 * resolving the batch, on purpose — it leaves the card live because the
 * answer route starts a continuation run when the user answers it later. So
 * disabling that card removed the only route back into the conversation for
 * it. `runStatus` does not reopen that hole: a timeout winds the run DOWN
 * through a successful tool result, so the tape's boundary is `run_finished`
 * and `deriveStatus` reports 'done', never 'failed'.
 *
 * FRONT-2106 adds `runStatus`, the same move FRONT-1850 made one component
 * over for the composer (`findBlockingQuestionItem`). It takes `deriveStatus`'s
 * ALREADY-DERIVED answer rather than the raw session row, precisely so this
 * is not a second copy of the "is this run dead" rule: `deriveStatus` folds
 * the tape's own run boundary and the session row together, and either signal
 * alone is enough here. That matters because the two arrive by different
 * routes — FRONT-2106's backend half puts a `run_failed` boundary on the tape
 * (`src/coding/store.ts`'s `failCodingSessions`), while the row status is what
 * a client that has not replayed the tape sees first.
 *
 * Why it is needed at all, given that boundary: `findOpenBatchOrBoundary`
 * short-circuits on it and so the composer and the status affordance recover
 * from the tape alone, but THIS predicate is per-item — it only ever sees one
 * batch's own fields, and a `run_failed` event elsewhere on the tape is
 * invisible to it. Without `runStatus` the card went on rendering live radios
 * and an enabled Submit against a run that no longer existed, and the user
 * discovered it was dead only by being refused ("These questions were already
 * answered", `src/coding/routes.ts`).
 *
 * `runStatus` is REQUIRED, not optional. An optional one would silently
 * default every un-updated call site back to treating a dead run as live —
 * the exact bug this closes — so the compiler is made to point at each one.
 */
export function isQuestionCardPending(input: {
  /** The tape's answers, or the optimistic local copy — either retires it. */
  answers: unknown[] | null | undefined;
  /** The tape said stopped, or the interview bar's stop just succeeded
   *  (FRONT-2478). */
  stopped: boolean;
  /** FRONT-2661: the tape said dismissed, or this card's Dismiss just
   *  succeeded. Closes the card without answers, like a stop. */
  dismissed: boolean;
  /** A submit came back 409: the run already consumed or superseded it. */
  superseded: boolean;
  /** `deriveStatus(session.status, events)` — NOT the raw session row.
   *  `undefined` only while the session's first status is still unknown, and
   *  an unknown run is treated as live (the card keeps working). */
  runStatus: CodingStatus | undefined;
}): boolean {
  if (input.runStatus === 'failed') return false;
  return !input.answers && !input.stopped && !input.dismissed && !input.superseded;
}

/** Pure — Skip only ever made sense on a real interview discovery question,
 *  never on the confirmsBuild go/adjust gate or a sparing 'build'-mode
 *  question — the go/adjust question is itself tagged mode:'interview'
 *  (FRONT-917), so confirmsBuild has to be checked explicitly here too. */
export function showSkipButton(mode: QuestionsMode, confirmsBuild: boolean): boolean {
  return mode === 'interview' && !confirmsBuild;
}

/**
 * Pure -- the status label shown in the card header (follow-up found live
 * 2026-09-04 while testing FRONT-1708). An older batch this session already
 * retired server-side (FRONT-715's cancelPendingBatches -- any newer
 * ask_user call unconditionally cancels every still-pending prior batch for
 * the session) used to read as a plain "pending" -- visually identical to a
 * genuinely still-open card, discoverable only by clicking through and
 * getting a 409. `interactive` (this item is the newest 'questions' item in
 * the transcript) is a SAFE signal for this LABEL, unlike for
 * isQuestionCardPending's form-gating above (see its own comment): by the
 * time a card is no longer the newest, its batch has unconditionally already
 * been cancelled server-side, so there is no live state this label could get
 * wrong by trusting it -- unlike form interactivity, which must stay
 * clickable for a batch whose OWN resolution (not a sibling's) hasn't
 * happened yet.
 */
export function questionCardStatusLabel(input: {
  answered: boolean;
  selfAnswered: boolean;
  stopped: boolean;
  /** FRONT-2661 — the card's own Dismiss. */
  dismissed: boolean;
  interactive: boolean;
  /** FRONT-2106 — same value `isQuestionCardPending` gets. Ordered BELOW the
   *  batch's own resolutions on purpose: a card the user really did answer
   *  before the backend died still reads "answered", because that is what
   *  happened. Only a card nothing ever resolved takes the run's label. */
  runStatus: CodingStatus | undefined;
}): string {
  if (input.answered) return input.selfAnswered ? 'auto-answered' : 'answered';
  if (input.stopped) return 'stopped';
  if (input.dismissed) return 'dismissed';
  // FRONT-2106: without this an unresolved card on a dead run read "waiting
  // for you" while having no Submit row left to act on — a worse lie than the
  // enabled-Submit bug it replaced, since it names the user as the blocker.
  if (input.runStatus === 'failed') return 'run failed';
  return input.interactive ? 'waiting for you' : 'superseded';
}

/**
 * The coding transcript's ask_user question card (FRONT-707) — since FRONT-3155
 * a READ-ONLY record. The questions stay in the transcript where they came up,
 * with their options as text; the answer form lives in the panel attached to
 * the reply box (`coding-answer-panel.tsx`), so this card has no radio, no
 * input and no button. State still comes straight from the tape: pending until
 * a question_answered event lands for its batch.
 */
export function CodingQuestionsCard({
  item,
  interactive,
  runStatus,
}: {
  item: QuestionsItem;
  /** Only the newest card is the one Eddy is waiting on — older ones are history. */
  interactive: boolean;
  /** `deriveStatus`'s verdict for the whole session (FRONT-2106) — a card
   *  whose run is dead cannot be answered, whatever its own fields say. */
  runStatus: CodingStatus | undefined;
}) {
  const answers = item.answers;
  const answered = !!answers;
  // FRONT-1667: a stop closes the card WITHOUT answers. Only the tape says so.
  const stopped = item.outcome === 'stopped';
  const dismissed = item.outcome === 'dismissed';
  const selfAnswered = item.outcome === 'self_answered';
  const pending = isQuestionCardPending({ answers, stopped, dismissed, superseded: false, runStatus });

  let headerIcon = <ChatCircleText size={14} weight="fill" className="text-primary" />;
  if (answered) headerIcon = <CheckCircle size={14} weight="fill" className="text-success" />;
  else if (stopped) headerIcon = <HandPalm size={14} weight="fill" className="text-muted-foreground" />;
  else if (dismissed) headerIcon = <XCircle size={14} weight="fill" className="text-muted-foreground" />;

  const tone = answered
    ? 'border-success/40 bg-success/5'
    : pending && interactive
      ? 'border-primary/40 bg-primary/5'
      : pending
        ? 'border-muted bg-muted/30'
        : 'border-muted bg-muted/30 opacity-80';

  return (
    // The one card a browser spec should look at is the open one Eddy waits on;
    // `interactive` is `item.seq === newestQuestionSeq`, so exactly one carries it.
    // Collapsed by default (eddy, 2026-10-26): the open questions are answered
    // in the panel at the reply box, so the transcript keeps one line per batch
    // ("3 questions · waiting for you") that opens to the full record. A native
    // <details>, so it needs no state and still works with JS off.
    <details
      className={`group/qc border rounded-lg ${tone}`}
      data-testid={pending && interactive ? 'coding-question-card-pending' : undefined}
      data-batch-id={item.batchId}
    >
      <summary className="px-3 py-2 flex cursor-pointer list-none items-center gap-2 rounded-lg outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        <CaretRight
          size={11}
          weight="bold"
          aria-hidden
          className="shrink-0 text-muted-foreground transition-transform group-open/qc:rotate-90 motion-reduce:transition-none"
        />
        {headerIcon}
        <span className="text-xs font-medium">
          {item.questions.length === 1 ? '1 question' : `${item.questions.length} questions`}
        </span>
        <span className="ml-auto text-[length:var(--text-2xs)] uppercase tracking-wider text-muted-foreground">
          {questionCardStatusLabel({
            answered,
            selfAnswered,
            stopped,
            dismissed,
            interactive,
            runStatus,
          })}
        </span>
      </summary>
      <div className="border-t border-inherit">
        {selfAnswered && (
          <div className="px-3 pt-2 text-[length:var(--text-xs)] text-muted-foreground">
            You were away, so EDDY went with its recommended options — easy to change later.
          </div>
        )}
        {stopped && (
          <div className="px-3 pt-2 text-[length:var(--text-xs)] text-muted-foreground">
            Guided questions are now off for this session — these were left unanswered.
          </div>
        )}
        {dismissed && !stopped && !answered && (
          <div className="px-3 pt-2 text-[length:var(--text-xs)] text-muted-foreground">
            You dismissed these questions. EDDY won&apos;t act on them.
          </div>
        )}
        <div className="px-3 py-3 space-y-3">
          {item.questions.map((q, qi) => {
            const answer = answers?.[qi];
            return (
              <div key={qi} className="space-y-1.5">
                <div className="flex items-center gap-2">
                  {q.header && (
                    <Badge variant="secondary" className="text-[length:var(--text-2xs)] font-medium">
                      {q.header}
                    </Badge>
                  )}
                  <span className="text-xs font-medium">{q.question}</span>
                </div>
                {answer?.skipped ? (
                  <div className="pl-1 text-xs italic text-muted-foreground">Skipped, EDDY used its judgment</div>
                ) : answer?.open ? (
                  <div className="pl-1 text-xs italic text-muted-foreground">
                    Still open, you can answer it from the next card
                  </div>
                ) : answer ? (
                  <AnsweredView answer={answer} />
                ) : (
                  <ul className="pl-1 space-y-1 text-xs text-foreground/90">
                    {q.options.map((opt) => {
                      const label = splitRecommended(opt.label);
                      return (
                        <li key={opt.label} className="flex items-start gap-1.5">
                          <span aria-hidden className="mt-1.5 size-1 shrink-0 rounded-full bg-muted-foreground/60" />
                          <span className="leading-tight text-pretty">
                            <span className="font-medium">{label.text}</span>
                            {label.recommended && <RecommendedChip />}
                            {opt.description && <span className="block text-muted-foreground">{opt.description}</span>}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
        {answered && item.note && (
          <div className="px-3 pb-3 text-xs text-muted-foreground">
            <span className="font-medium text-foreground/80">Your note:</span> {item.note}
          </div>
        )}
      </div>
    </details>
  );
}
